/**
 * LuckyMenu's own product site (P12-T20) and its legal pages (P12-T21),
 * served by the storefront for `www.<root>` and the bare root domain
 * (storefront-sites.md). No zod: the browser bundles import it.
 */

/** Paths of the legal pages on the product site. */
export const LEGAL_PAGES = { privacy: "/privacy", terms: "/terms" } as const;
export type LegalPage = keyof typeof LEGAL_PAGES;

/** The demo restaurant, linked from the product site (P12-T22). */
export const DEMO_RESTAURANT_SLUG = "demo";

/** The environments, as `@luckymenu/config` names them. */
export type ProductSiteEnv = "development" | "test" | "production";

/**
 * The product site's origin: `https://www.<root>` (the bare `test.` domain
 * has no DNS record, Q18), locally the storefront's port.
 */
export function productSiteOrigin(appEnv: ProductSiteEnv, rootDomain: string): string {
	return appEnv === "development" ? `http://www.${rootDomain}:4010` : `https://www.${rootDomain}`;
}

/** A restaurant site's origin, for the product site's demo link. */
export function restaurantSiteOrigin(
	appEnv: ProductSiteEnv,
	rootDomain: string,
	slug: string,
): string {
	return appEnv === "development"
		? `http://${slug}.${rootDomain}:4010`
		: `https://${slug}.${rootDomain}`;
}

/**
 * The privacy policy and terms are placeholders marked "draft" until the
 * owner provides reviewed text (open question Q9). They are shown on test
 * and in development only; in production the pages answer 404 and no app
 * links to them, so nothing unreviewed is published as our terms.
 */
export function legalPagesPublished(appEnv: ProductSiteEnv): boolean {
	return appEnv !== "production";
}

/** Absolute links to the legal pages, or `null` while they are not published. */
export function legalLinks(
	appEnv: ProductSiteEnv,
	rootDomain: string,
): Record<LegalPage, string> | null {
	if (!legalPagesPublished(appEnv)) return null;
	const origin = productSiteOrigin(appEnv, rootDomain);
	return { privacy: `${origin}${LEGAL_PAGES.privacy}`, terms: `${origin}${LEGAL_PAGES.terms}` };
}
